// src/pages/Settings/SettingsController.ts
import {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {getExchangeRates, updateExchangeRate} from '../../services/Rates'
import {getThemeSettings, updateThemeSetting} from '../../services/Theme'
import {getUssdConfig, updateUssdConfig} from '../../services/Ussd'
import {api, isAuthenticated} from '../../services/api'

// أنواع البيانات
interface ExchangeRate {
    id: number
    name: string
    code: string
    rate_lbp: number
    description: string
    updated_at: string
}

interface ThemeSetting {
    id: number
    key: string
    value: string
    type: 'text' | 'color' | 'image' | 'font' | 'number' | 'boolean'
    category: 'general' | 'colors' | 'fonts' | 'images' | 'layout' | 'operators'
    description: string
    updated_at: string
}

interface UssdConfig {
    id: number
    name: string
    code: string
    value: string
    description: string
    updated_at: string
}

type ActiveTab = 'rates' | 'wallets' | 'theme' | 'ussd' | 'system'

export const useSettingsController = () => {
    const navigate = useNavigate()
    const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([])
    const [themeSettings, setThemeSettings] = useState<any>({}) // كائن وليس مصفوفة
    const [ussdConfig, setUssdConfig] = useState<UssdConfig[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState<Record<string, boolean>>({})
    const [messages, setMessages] = useState<Record<string, {type: 'success' | 'error', text: string}>>({})
    const [activeTab, setActiveTab] = useState<ActiveTab>('rates')
    const [sidebarOpen, setSidebarOpen] = useState(false)

    // 👇 Toast Message جديد
    const [toastMessage, setToastMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)

    // Toggle states
    const [notificationsEnabled, setNotificationsEnabled] = useState(false)
    const [twoFAEnabled, setTwoFAEnabled] = useState(false)
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)

    useEffect(() => {
        if (!isAuthenticated()) {
            console.log('🚫 Not authenticated, redirecting to login')
            navigate('/login')
            return
        }
        console.log('🔄 Fetching settings data...')
        fetchAllSettings()
    }, [])

    const fetchAllSettings = async () => {
        setLoading(true)
        try {
            console.log('📡 Fetching exchange rates...')
            const rates = await getExchangeRates()
            console.log('✅ Exchange rates:', rates)
            setExchangeRates(rates)

            console.log('📡 Fetching theme settings...')
            const theme = await getThemeSettings()
            console.log('✅ Theme settings:', theme)
            setThemeSettings(theme)

            console.log('📡 Fetching USSD config...')
            const ussd = await getUssdConfig()
            console.log('✅ USSD config:', ussd)
            setUssdConfig(ussd)

            // Toggles (اختياري)
            try {
                const togglesResponse = await api.get('/api/admin/settings/toggles')
                if (togglesResponse.data.success) {
                    setNotificationsEnabled(togglesResponse.data.data?.notifications ?? false)
                    setTwoFAEnabled(togglesResponse.data.data?.twoFA ?? false)
                    setAutoRefreshEnabled(togglesResponse.data.data?.autoRefresh ?? true)
                }
            } catch (toggleError) {
                console.log('⚠️ Toggles endpoint not available, using defaults')
            }

        } catch (error: any) {
            console.error('❌ Error fetching settings:', error)

            setToastMessage({
                type: 'error',
                text: 'فشل تحميل الإعدادات. يرجى المحاولة مرة أخرى.'
            })
            setTimeout(() => setToastMessage(null), 3000)

            if (error?.response?.status === 401) {
                console.log('🔒 Unauthorized, redirecting to login')
                navigate('/login')
                return
            }
        } finally {
            setLoading(false)
        }
    }

    const handleRateChange = (code: string, value: string) => {
        setExchangeRates(prev =>
            prev.map(rate => rate.code === code ? {...rate, rate_lbp: parseInt(value) || 0} : rate)
        )
    }

    const handleThemeChange = (key: string, value: string) => {
        // themeSettings هو كائن، نحتاج نبحث في كل الفئات
        const newThemeSettings = {...themeSettings}

        // دور على الإعداد في كل الفئات
        Object.keys(newThemeSettings).forEach(category => {
            const items = newThemeSettings[category]
            if (Array.isArray(items)) {
                const index = items.findIndex((item: ThemeSetting) => item.key === key)
                if (index !== -1) {
                    items[index] = {...items[index], value}
                }
            }
        })

        setThemeSettings(newThemeSettings)
    }

    const handleUssdChange = (code: string, value: string) => {
        setUssdConfig(prev =>
            prev.map(config => config.code === code ? {...config, value} : config)
        )
    }

    const handleSaveRate = async (code: string) => {
        setSaving(prev => ({...prev, [code]: true}))
        try {
            const rate = exchangeRates.find(r => r.code === code)
            if (!rate) return

            console.log(`💾 Saving rate ${code}:`, rate.rate_lbp)
            const success = await updateExchangeRate(code, rate.rate_lbp)

            if (success) {
                setToastMessage({
                    type: 'success',
                    text: 'تم حفظ السعر بنجاح'
                })
                setTimeout(() => setToastMessage(null), 3000)
            } else {
                setToastMessage({
                    type: 'error',
                    text: 'فشل حفظ السعر'
                })
                setTimeout(() => setToastMessage(null), 3000)
            }
        } catch (error) {
            console.error('Error saving rate:', error)
            setToastMessage({
                type: 'error',
                text: 'حدث خطأ في الاتصال'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } finally {
            setSaving(prev => ({...prev, [code]: false}))
        }
    }

    const handleSaveTheme = async (key: string) => {
        setSaving(prev => ({...prev, [key]: true}))
        try {
            // البحث عن الإعداد في الكائن
            let setting: ThemeSetting | undefined
            Object.keys(themeSettings).forEach(category => {
                const items = themeSettings[category]
                if (Array.isArray(items)) {
                    const found = items.find((item: ThemeSetting) => item.key === key)
                    if (found) setting = found
                }
            })

            if (!setting) return

            console.log(`💾 Saving theme ${key}:`, setting.value)
            const success = await updateThemeSetting(key, setting.value)

            if (success) {
                setToastMessage({
                    type: 'success',
                    text: 'تم حفظ إعداد الثيم بنجاح'
                })
                setTimeout(() => setToastMessage(null), 3000)
            }
        } catch (error) {
            console.error('Error saving theme:', error)
            setToastMessage({
                type: 'error',
                text: 'حدث خطأ في الاتصال'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } finally {
            setSaving(prev => ({...prev, [key]: false}))
        }
    }

    const handleSaveUssd = async (code: string) => {
        setSaving(prev => ({...prev, [code]: true}))
        try {
            const config = ussdConfig.find(c => c.code === code)
            if (!config) return

            console.log(`💾 Saving USSD ${code}:`, config.value)
            const success = await updateUssdConfig(code, config.value)

            if (success) {
                setToastMessage({
                    type: 'success',
                    text: 'تم حفظ إعداد USSD بنجاح'
                })
                setTimeout(() => setToastMessage(null), 3000)
            }
        } catch (error) {
            console.error('Error saving USSD:', error)
            setToastMessage({
                type: 'error',
                text: 'حدث خطأ في الاتصال'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } finally {
            setSaving(prev => ({...prev, [code]: false}))
        }
    }

    const handleToggleNotifications = async (value: boolean) => {
        try {
            console.log('🔔 Toggling notifications:', value)
            setNotificationsEnabled(value)
            await api.post('/api/admin/settings/toggles', {
                key: 'enable_notifications',
                value: value
            })
            setToastMessage({
                type: 'success',
                text: value ? 'تم تفعيل الإشعارات' : 'تم إيقاف الإشعارات'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } catch (error) {
            console.error('Error toggling notifications:', error)
            setNotificationsEnabled(!value)
            setToastMessage({
                type: 'error',
                text: 'حدث خطأ في تحديث الإعدادات'
            })
            setTimeout(() => setToastMessage(null), 3000)
        }
    }

    const handleToggle2FA = async (value: boolean) => {
        try {
            console.log('🔐 Toggling 2FA:', value)
            setTwoFAEnabled(value)
            await api.post('/api/admin/settings/toggles', {
                key: 'enable_2fa',
                value: value
            })
            setToastMessage({
                type: 'success',
                text: value ? 'تم تفعيل المصادقة الثنائية' : 'تم إيقاف المصادقة الثنائية'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } catch (error) {
            console.error('Error toggling 2FA:', error)
            setTwoFAEnabled(!value)
            setToastMessage({
                type: 'error',
                text: 'حدث خطأ في تحديث الإعدادات'
            })
            setTimeout(() => setToastMessage(null), 3000)
        }
    }

    const handleToggleAutoRefresh = async (value: boolean) => {
        try {
            console.log('🔄 Toggling auto refresh:', value)
            setAutoRefreshEnabled(value)
            await api.post('/api/admin/settings/toggles', {
                key: 'auto_refresh',
                value: value
            })
            setToastMessage({
                type: 'success',
                text: value ? 'تم تفعيل التحديث التلقائي' : 'تم إيقاف التحديث التلقائي'
            })
            setTimeout(() => setToastMessage(null), 3000)
        } catch (error) {
            console.error('Error toggling auto refresh:', error)
            setAutoRefreshEnabled(!value)
            setToastMessage({
                type: 'error',
                text: 'حدث خطأ في تحديث الإعدادات'
            })
            setTimeout(() => setToastMessage(null), 3000)
        }
    }

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen)
    }

    const handleTabChange = (tab: ActiveTab) => {
        console.log('📌 Changing tab to:', tab)
        setActiveTab(tab)
    }

    const clearMessage = (key: string) => {
        setMessages(prev => {
            const newMessages = {...prev}
            delete newMessages[key]
            return newMessages
        })
    }

    return {
        // State
        exchangeRates,
        themeSettings,
        ussdConfig,
        loading,
        saving,
        messages,
        activeTab,
        sidebarOpen,
        notificationsEnabled,
        twoFAEnabled,
        autoRefreshEnabled,
        toastMessage,

        // Actions
        handleRateChange,
        handleThemeChange,
        handleUssdChange,
        handleSaveRate,
        handleSaveTheme,
        handleSaveUssd,
        toggleSidebar,
        handleTabChange,
        clearMessage,
        handleToggleNotifications,
        handleToggle2FA,
        handleToggleAutoRefresh
    }
}